import * as React from "react"
import Select from "react-select"
import JobForm from "./JobFormContext"
import { payIsValid } from "./payValidity"

// Unit labels read as a sentence: "$30 to $45 per Hour".
const PAY_UNIT_OPTIONS = [
  { value: "hourly", label: "Hour" },
  { value: "daily", label: "Day" },
  { value: "monthly", label: "Month" },
  { value: "annually", label: "Year" },
];

export default function Compensation({ tabIndexFunc, benefitsClass = "" }) {
  const [jobState, setJobState] = React.useContext(JobForm)
  // Tried to continue
  const [tried, setTried] = React.useState(false)

  const benefitHandler = e => {
    const benefitMapping = {
      "bonuses": "benefit_bonuses",
      "medical": "benefit_medical",
      "dental": "benefit_dental",
      "vision": "benefit_vision",
      "fourk": "benefit_401k",
      "pto": "benefit_pto"
    };

    const key = benefitMapping[e];
    if (key) {
      setJobState({ ...jobState, [key]: !jobState[key] });
    }
  }

  //sets pay in the job state
  const inputHandler = (e, type) => {
    if (type === "starting") {
      setJobState({ ...jobState, min_compensation: e.target.value })
    }

    if (type === "ending") {
      setJobState({ ...jobState, compensation: e.target.value })
    }
  }

  // Explicit pay mode. Pay is required on every posting now, so there are only
  // two: a range, or a single fixed amount (exact_pay). "Depends on
  // experience" is gone from the form; legacy rows that still carry doe: true
  // land on an empty range the employer has to fill in.
  const payMode = jobState.exact_pay ? "exact" : "range"

  const setPayMode = (mode) => {
    if (mode === "range") setJobState({ ...jobState, exact_pay: false, doe: false })
    if (mode === "exact") setJobState({ ...jobState, exact_pay: true, doe: false, compensation: null })
  }

  // A legacy DOE job opened for editing must not post doe: true back. Drop the
  // flag on load; the amounts are blank, so the form settles on an empty range
  // that Continue refuses until it is filled in.
  React.useEffect(() => {
    if (jobState.doe) setJobState({ ...jobState, doe: false })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobState.doe])

  const filled = (value: any) =>
    value !== null && value !== undefined && value !== "" && Number(value) > 0

  // Field-level flags drive which input gets the red ring and which message
  // shows. The overall verdict comes from the shared payIsValid() so this step
  // and the parent form can never disagree, and so the parent's own validation
  // effect has no pay flag left to overwrite.
  const min_compensation_is_valid = filled(jobState.min_compensation)
  const compensation_is_valid = payMode !== "range" || filled(jobState.compensation)
  const maxBelowMin = payMode === "range" &&
    filled(jobState.min_compensation) && filled(jobState.compensation) &&
    Number(jobState.compensation) < Number(jobState.min_compensation)

  const payValid = payIsValid(jobState)

  return (
    <div className="job-builder-step job-builder-step--compensation">
      <div className="job-builder-step__summary">
        <div>
          <strong>Set clear expectations</strong>
          <p>Listings with useful pay details tend to attract more confident applicants.</p>
        </div>
        <i className="fa fa-chart-line" aria-hidden="true"></i>
      </div>
      <div className={`form-group py-2${tried && !payValid ? " has-error" : ""}`}>
        <h4 className="font-weight-bold">Pay</h4>
        <p className="job-builder-field-help">How do you want to share pay with candidates? Pick one:</p>
        <div className="job-builder-paymode" role="radiogroup" aria-label="How pay is shared">
          {[
            { key: "range", label: "Pay range", note: "Show a minimum and maximum" },
            { key: "exact", label: "Exact amount", note: "One set rate" },
          ]
            .map(m => (
              <button
                key={m.key}
                type="button"
                role="radio"
                aria-checked={payMode === m.key}
                className={`job-builder-paymode__card${payMode === m.key ? " active" : ""}`}
                onClick={() => setPayMode(m.key)}
              >
                <span className="job-builder-paymode__radio" aria-hidden="true"></span>
                <span className="job-builder-paymode__text">
                  <strong>{m.label}</strong>
                  <small>{m.note}</small>
                </span>
              </button>
            ))}
        </div>

        <div className="job-builder-pay-sentence">
          <span className="job-builder-pay-amount">
            <span className="job-builder-pay-amount__currency" aria-hidden="true">$</span>
            <input
              className={`form-control${tried && !min_compensation_is_valid ? " is-invalid" : ""}`}
              type="number"
              min="0"
              placeholder="0"
              aria-label={payMode === "exact" ? "Amount in dollars" : "Minimum in dollars"}
              onChange={e => inputHandler(e, "starting")}
              value={jobState.min_compensation ?? ""}
            />
          </span>
          {payMode === "range" && (
            <>
              <span className="job-builder-pay-sentence__word">to</span>
              <span className="job-builder-pay-amount">
                <span className="job-builder-pay-amount__currency" aria-hidden="true">$</span>
                <input
                  className={`form-control${tried && (!compensation_is_valid || maxBelowMin) ? " is-invalid" : ""}`}
                  type="number"
                  min="0"
                  placeholder="0"
                  aria-label="Maximum in dollars"
                  onChange={e => inputHandler(e, "ending")}
                  value={jobState.compensation ?? ""}
                />
              </span>
            </>
          )}
          <span className="job-builder-pay-sentence__per">
            <span className="job-builder-pay-sentence__word">per</span>
            <div className="job-builder-pay-sentence__unit">
              <Select
                options={PAY_UNIT_OPTIONS}
                value={PAY_UNIT_OPTIONS.find(o => o.value === jobState.compensation_type) || PAY_UNIT_OPTIONS[0]}
                onChange={(selected) => setJobState({ ...jobState, compensation_type: selected ? selected.value : "hourly" })}
                classNamePrefix="react-select"
                isClearable={false}
                isSearchable={false}
                inputId="compensation_type"
              />
            </div>
          </span>
        </div>

        {tried && maxBelowMin && (
          <p className="job-builder-field-error" role="alert">Max should be at least the minimum.</p>
        )}
        {tried && !maxBelowMin && !payValid && payMode === "range" && (
          <p className="job-builder-field-error" role="alert">Enter a pay range.</p>
        )}
        {tried && !payValid && payMode === "exact" && (
          <p className="job-builder-field-error" role="alert">Enter an amount.</p>
        )}

        <div className="job-builder-pay-note">
          <strong>Why pay is required</strong>
          <p>Postings without pay get fewer views and applicants, and job boards rank them lower.</p>
          <p>
            A growing list of states require employers to include a pay range in job ads,
            including California, Colorado, Washington, New York, Illinois, Hawaii, and Maryland.
          </p>
        </div>
      </div>

      {
        jobState.position_type !== "temp" && <div className="form-group py-2">
          <h4 className="font-weight-bold">
            Benefits
          </h4>

          <p className="job-builder-field-help">Select every benefit included with this role.</p>
          <div className={`job-builder-benefits ${benefitsClass}`}>
            <div className="form-check ps-0 ms-0">
              <input
                className="form-check-input btn-check"
                type="checkbox"
                id="flexCheckBonuses"
                checked={jobState.benefit_bonuses}
                onChange={e => benefitHandler("bonuses")}
              />
              <label
                className="form-check-label btn text-light rounded px-2 py-1 me-1 mb-1"
                htmlFor="flexCheckBonuses"
                style={
                  jobState.benefit_bonuses
                    ? { backgroundColor: "green" }
                    : { backgroundColor: "green", opacity: 0.65 }
                }
              >
                {jobState.benefit_bonuses ? (
                  <i className="fa fa-check text-white" aria-hidden="true"></i>
                ) : (
                  ""
                )}{" "}
                Bonuses
              </label>
            </div>
            <div className="form-check ps-0 ms-0">
              <input
                className="form-check-input btn-check"
                type="checkbox"
                id="flexCheckMedical"
                checked={jobState.benefit_medical}
                onChange={e => benefitHandler("medical")}
              />
              <label
                className="form-check-label btn text-light rounded px-2 py-1 me-1 mb-1"
                htmlFor="flexCheckMedical"
                style={
                  jobState.benefit_medical
                    ? { backgroundColor: "green" }
                    : { backgroundColor: "green", opacity: 0.65 }
                }
              >
                {jobState.benefit_medical ? (
                  <i className="fa fa-check text-white" aria-hidden="true"></i>
                ) : (
                  ""
                )}{" "}
                Medical
              </label>
            </div>
            <div className="form-check ps-0 ms-0">
              <input
                className="form-check-input btn-check"
                type="checkbox"
                id="flexCheckDental"
                checked={jobState.benefit_dental}
                onChange={e => benefitHandler("dental")}
              />
              <label
                className="form-check-label btn text-light rounded px-2 py-1 me-1 mb-1"
                htmlFor="flexCheckDental"
                style={
                  jobState.benefit_dental
                    ? { backgroundColor: "green" }
                    : { backgroundColor: "green", opacity: 0.65 }
                }
              >
                {jobState.benefit_dental ? (
                  <i className="fa fa-check text-white" aria-hidden="true"></i>
                ) : (
                  ""
                )}{" "}
                Dental
              </label>
            </div>
            <div className="form-check ps-0 ms-0">
              <input
                className="form-check-input btn-check"
                type="checkbox"
                id="flexCheckVision"
                checked={jobState.benefit_vision}
                onChange={e => benefitHandler("vision")}
              />
              <label
                className="form-check-label btn text-light rounded px-2 py-1 me-1 mb-1"
                htmlFor="flexCheckVision"
                style={
                  jobState.benefit_vision
                    ? { backgroundColor: "green" }
                    : { backgroundColor: "green", opacity: 0.65 }
                }
              >
                {jobState.benefit_vision ? (
                  <i className="fa fa-check text-white" aria-hidden="true"></i>
                ) : (
                  ""
                )}{" "}
                Vision
              </label>
            </div>
            <div className="form-check ps-0 ms-0">
              <input
                className="form-check-input btn-check"
                type="checkbox"
                id="flexCheckFourk"
                checked={jobState.benefit_401k}
                onChange={e => benefitHandler("fourk")}
              />
              <label
                className="form-check-label btn text-light rounded px-2 py-1 me-1 mb-1"
                htmlFor="flexCheckFourk"
                style={
                  jobState.benefit_401k
                    ? { backgroundColor: "green" }
                    : { backgroundColor: "green", opacity: 0.65 }
                }
              >
                {jobState.benefit_401k ? (
                  <i className="fa fa-check text-white" aria-hidden="true"></i>
                ) : (
                  ""
                )}{" "}
                401K
              </label>
            </div>
            <div className="form-check ps-0 ms-0">
              <input
                className="form-check-input btn-check"
                type="checkbox"
                id="flexCheckPto"
                checked={jobState.benefit_pto}
                onChange={e => benefitHandler("pto")}
              />
              <label
                className="form-check-label btn text-light rounded px-2 py-1 me-1 mb-1"
                htmlFor="flexCheckPto"
                style={
                  jobState.benefit_pto
                    ? { backgroundColor: "green" }
                    : { backgroundColor: "green", opacity: 0.65 }
                }
              >
                {jobState.benefit_pto ? (
                  <i className="fa fa-check text-white" aria-hidden="true"></i>
                ) : (
                  ""
                )}{" "}
                PTO
              </label>
            </div>
          </div>
        </div>
      }

      <div className="job-builder-actions">
        <button
          type="button"
          className="job-builder-button job-builder-button--secondary"
          onClick={() => {
            tabIndexFunc(1)
          }}
        >
          <i className="fa fa-arrow-left" aria-hidden="true"></i>
          Back
        </button>
        <button
          type="button"
          className="job-builder-button job-builder-button--primary"
          onClick={() => {
            setTried(true)
            if (payValid) {
              tabIndexFunc(3)
            }
          }}
        >
          Continue to description
          <i className="fa fa-arrow-right" aria-hidden="true"></i>
        </button>
      </div>
    </div>
  )
}
